"use client";

import { c as _c } from "react/compiler-runtime";
import ChevronDownIcon from "@/assets/icons/chevron_down.svg";
import ChevronLeftIcon from "@/assets/icons/chevron-left.svg";
import ChevronRightIcon from "@/assets/icons/chevron-right.svg";
import CloseIcon from "@/assets/icons/close.svg";
import { formatUtcDate, isSameUtcDay, parseCalendarDate, startOfUtcDay } from "@/lib/date-utils";
import { cn } from "@/lib/utils";
import { Popover } from "@mantine/core";
import { DatePicker, type DateStringValue } from "@mantine/dates";
import { useState } from "react";
import { clampMonth, getMonthBounds } from "../../month-bounds";
import { getInitialStatsRange, type DateRange } from "../../stream-stats-range";
import styles from "./month-picker.module.scss";
export type DateRangePickerSelectProps = {
  value: DateRange;
  onChange: (range: DateRange) => void;
  className?: string;
};
const MAX_RANGE_DAYS = 31;
type MantineDateRange = [DateStringValue | null, DateStringValue | null];
const calendarClassNames = {
  calendarHeader: styles.calendarHeader,
  calendarHeaderLevel: styles.calendarMonthLabel,
  calendarHeaderControl: styles.calendarNavButton,
  monthThead: styles.monthThead,
  weekday: styles.weekday,
  month: styles.month,
  monthCell: styles.monthCell,
  day: styles.day
};
function fromDateString(value: DateStringValue): Date {
  return parseCalendarDate(value);
}
function toDateString(value: Date): DateStringValue {
  return formatUtcDate(value, "yyyy-MM-dd");
}
function toRange(range: DateRange): MantineDateRange {
  return [toDateString(range.from), toDateString(range.to)];
}
function isBeyondMaxRange(from: Date, date: DateStringValue): boolean {
  const picked = fromDateString(date);
  const dayMs = 86_400_000;
  const fromUtc = Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate());
  const pickedUtc = Date.UTC(picked.getUTCFullYear(), picked.getUTCMonth(), picked.getUTCDate());
  return Math.round(Math.abs(pickedUtc - fromUtc) / dayMs) >= MAX_RANGE_DAYS;
}
export function DateRangePickerSelect(t0) {
  const $ = _c(59);
  const {
    value,
    onChange,
    className
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    const {
      minMonth
    } = getMonthBounds();
    t1 = toDateString(minMonth);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const minDate = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = toDateString(startOfUtcDay(new Date()));
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const maxDate = t2;
  const [open, setOpen] = useState(false);
  let t3;
  if ($[2] !== value.from) {
    t3 = () => toDateString(clampMonth(value.from));
    $[2] = value.from;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const [displayMonth, setDisplayMonth] = useState(t3);
  let t4;
  if ($[4] !== value) {
    t4 = () => toRange(value);
    $[4] = value;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const [range, setRange] = useState(t4);
  let defaultRange;
  let t5;
  let today;
  if ($[6] !== value.from || $[7] !== value.to) {
    today = startOfUtcDay(new Date());
    defaultRange = getInitialStatsRange(today);
    t5 = isSameUtcDay(value.from, defaultRange.from) && isSameUtcDay(value.to, defaultRange.to);
    $[6] = value.from;
    $[7] = value.to;
    $[8] = defaultRange;
    $[9] = t5;
    $[10] = today;
  } else {
    defaultRange = $[8];
    t5 = $[9];
    today = $[10];
  }
  const isLast30Days = t5;
  let t6;
  if ($[11] !== value.from) {
    t6 = formatUtcDate(value.from, "MMM d");
    $[11] = value.from;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== value.to) {
    t7 = formatUtcDate(value.to, "MMM d");
    $[13] = value.to;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let triggerLabel = `${t6} – ${t7}`;
  if (isLast30Days) {
    triggerLabel = "Last 30 days";
  } else {
    if (isSameUtcDay(value.from, value.to)) {
      let t8;
      if ($[15] !== value.from) {
        t8 = formatUtcDate(value.from, "MMM d");
        $[15] = value.from;
        $[16] = t8;
      } else {
        t8 = $[16];
      }
      triggerLabel = t8;
    }
  }
  let t8;
  if ($[17] !== value) {
    t8 = function handleOpenChange(next) {
      if (!next) {
        setRange(toRange(value));
      }
      setOpen(next);
    };
    $[17] = value;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const handleOpenChange = t8;
  let t9;
  if ($[19] !== onChange) {
    t9 = function handleSelect(nextRange) {
      const [from, to] = nextRange;
      if (!from) {
        return;
      }
      setRange(nextRange);
      if (to) {
        onChange({
          from: fromDateString(from),
          to: fromDateString(to)
        });
        setOpen(false);
      }
    };
    $[19] = onChange;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const handleSelect = t9;
  let t10;
  if ($[21] !== defaultRange || $[22] !== onChange || $[23] !== today) {
    t10 = function handleReset(e) {
      e.stopPropagation();
      setRange(toRange(defaultRange));
      setDisplayMonth(toDateString(clampMonth(today)));
      onChange(defaultRange);
    };
    $[21] = defaultRange;
    $[22] = onChange;
    $[23] = today;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const handleReset = t10;
  const [rangeFrom] = range;
  const t11 = open ? "open" : "closed";
  let t12;
  if ($[25] !== className) {
    t12 = cn(styles.trigger, className);
    $[25] = className;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== handleOpenChange || $[28] !== open) {
    t13 = () => handleOpenChange(!open);
    $[27] = handleOpenChange;
    $[28] = open;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== triggerLabel) {
    t14 = <span className={styles.triggerLabel}>{triggerLabel}</span>;
    $[30] = triggerLabel;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== handleReset || $[33] !== isLast30Days) {
    t15 = !isLast30Days ? <span role="button" aria-label="Reset to last 30 days" onClick={handleReset} className={styles.resetButton}><CloseIcon className={styles.resetIcon} aria-hidden={true} /></span> : <ChevronDownIcon className={styles.triggerChevron} aria-hidden={true} />;
    $[32] = handleReset;
    $[33] = isLast30Days;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== open || $[36] !== t11 || $[37] !== t12 || $[38] !== t13 || $[39] !== t14 || $[40] !== t15) {
    t16 = <Popover.Target><button type="button" aria-label="Select date range" aria-haspopup="dialog" aria-expanded={open} data-state={t11} className={t12} onClick={t13}>{t14}{t15}</button></Popover.Target>;
    $[35] = open;
    $[36] = t11;
    $[37] = t12;
    $[38] = t13;
    $[39] = t14;
    $[40] = t15;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = month => setDisplayMonth(toDateString(clampMonth(fromDateString(month))));
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== range[1] || $[44] !== rangeFrom) {
    t18 = rangeFrom && !range[1] ? date => isBeyondMaxRange(fromDateString(rangeFrom), date) : undefined;
    $[43] = range[1];
    $[44] = rangeFrom;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = ["level", "previous", "next"];
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  let t21;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <ChevronLeftIcon className={styles.calendarNavIcon} aria-hidden={true} />;
    t21 = <ChevronRightIcon className={styles.calendarNavIcon} aria-hidden={true} />;
    $[47] = t20;
    $[48] = t21;
  } else {
    t20 = $[47];
    t21 = $[48];
  }
  let t22;
  if ($[49] !== displayMonth || $[50] !== handleSelect || $[51] !== range || $[52] !== t18) {
    t22 = <Popover.Dropdown className={styles.popoverContent}><DatePicker type="range" classNames={calendarClassNames} value={range} onChange={handleSelect} date={displayMonth} onDateChange={t17} minDate={minDate} maxDate={maxDate} maxLevel="month" hideOutsideDates={true} allowSingleDateInRange={true} excludeDate={t18} headerControlsOrder={t19} monthLabelFormat="MMMM YYYY" weekdayFormat="ddd" previousIcon={t20} nextIcon={t21} withCellSpacing={false} fullWidth={true} /></Popover.Dropdown>;
    $[49] = displayMonth;
    $[50] = handleSelect;
    $[51] = range;
    $[52] = t18;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  let t23;
  if ($[54] !== handleOpenChange || $[55] !== open || $[56] !== t16 || $[57] !== t22) {
    t23 = <Popover opened={open} onChange={handleOpenChange} position="bottom-end" offset={8} withinPortal={true} width={357}>{t16}{t22}</Popover>;
    $[54] = handleOpenChange;
    $[55] = open;
    $[56] = t16;
    $[57] = t22;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  return t23;
}